import { cloneDeep, isNumber, isString, maxBy, minBy, toInteger } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
    BrushType,
    IBrushLayerProps,
    IImageLayerProps,
    IImageTransform,
    ILayerProps,
    ILinearGradient,
    IMAGE_LAYER_CANVAS_ID_PREFIX,
    IMarkupLayerProps,
    IPoint,
    IPosition,
    ISize,
    ITransform,
    IVectorLayerProps,
    LayerType,
    MarkupType,
    MaterialIdsType,
    StretchOptionType,
    VectorType,
    type BrushData,
    type ILayerInfo,
    type IPointsInfo,
} from '@/version-types';
import { getRotatePosition } from './collision-kit';
import { createCanvas, getImgInfoAsync } from './file-kit';
import { addPoint, rotatePoint, transferPointToInt, translatePointAngle, translatePointMove } from './math-kit';
import { traverseTree } from './tree-kit';

/**
 * 判断是否根据搞度自适应
 *
 * @param oldSize - 旧的尺寸大小
 * @param newSize - 新的尺寸大小
 */
export function isFitHeight(oldSize: ISize, newSize: ISize) {
    const oldRatio = Math.floor((oldSize.width / oldSize.height) * 10000000000);
    const newRatio = Math.floor((newSize.width / newSize.height) * 10000000000);
    return oldRatio < newRatio;
}

/**
 * 判断图层是否为空效果
 *
 * @param layerData - 图层数据
 */
export const isEmptyEffect = (layerData: IImageLayerProps) => {
    return !(
        layerData.effect &&
        (layerData.effect.stroke.length > 0 || layerData.effect.glow.length > 0 || layerData.effect.shadow.length > 0)
    );
};

/**
 * 设置图层运行时值
 *
 * @param layer - 图层属性
 * @param obj - 数据对象
 */
export function setLayerRuntimeValue(layer: ILayerProps, obj: Record<string, any>) {
    layer.__runtime = {
        ...(layer.__runtime || {}),
        ...obj,
    };
}

/**
 * 获取图层运行时值
 *
 * @param layer - 图层属性
 * @param key  - 键名
 */
export function getLayerRuntimeValue<T>(layer: ILayerProps, key: string): T | undefined {
    return (layer.__runtime || {})[key];
}

interface IComputerImageTransform {
    imageTransform: IImageTransform;
    url: string;
    naturalWidth: number;
    naturalHeight: number;
    stretchOption: StretchOptionType;
}

export const computerImageTransform = (
    newWidth: number,
    newHeight: number,
    { url, naturalWidth, stretchOption, naturalHeight, imageTransform }: IComputerImageTransform,
) => {
    imageTransform.offsetX = 0;
    imageTransform.offsetY = 0;
    imageTransform.flipH = false;
    imageTransform.flipV = false;
    const widthRadio = newWidth / naturalWidth;
    const heightRadio = newHeight / naturalHeight;
    imageTransform.scale = 1;
    if (stretchOption !== StretchOptionType.repeat) {
        if (widthRadio > heightRadio) {
            imageTransform.widthRatio = 1;
            imageTransform.heightRatio = (naturalHeight * widthRadio) / newHeight;
        } else {
            imageTransform.heightRatio = 1;
            imageTransform.widthRatio = (naturalWidth * heightRadio) / newWidth;
        }
    }
};

interface imgRatioParam {
    imgWidth: number;
    imgHeight: number;
    stageWidth: number;
    stageHeight: number;
}

/**
 * 计算图片比例
 *
 * @param para - 参数
 */
export const computerBgRatio = (para: imgRatioParam) => {
    const { imgWidth, imgHeight, stageHeight, stageWidth } = para;
    const imgRatio = imgWidth / imgHeight;
    const stageRatio = stageWidth / stageHeight;
    let widthRatio = 1;
    let heightRatio = 1;

    if (imgRatio < stageRatio) {
        heightRatio = +(((stageWidth / imgWidth) * imgHeight) / stageHeight).toFixed(4);
    } else if (imgRatio > stageRatio) {
        widthRatio = +(((stageHeight / imgHeight) * imgWidth) / stageWidth).toFixed(4);
    }
    return { widthRatio, heightRatio };
};

/**
 * 移动图层到指定的位置 —— 调用该方法前需要换算图层的旋转角度
 *
 * @param layer - 图层属性
 * @param left - 左侧位置
 * @param top - 顶部位置
 */
export function moveLayerTo(layer: ILayerProps, left: number, top: number) {
    const { left: cLeft, top: cTop } = layer;
    const offsetX = left - cLeft;
    const offsetY = top - cTop;
    layer.left = left;
    layer.top = top;
    (layer.children || []).forEach((item) => moveLayerOffset(item, offsetX, offsetY));
}

/**
 * 图层移动相应的偏移量
 *
 * @param layer - 图层属性
 * @param offsetX - 水平偏移量
 * @param offsetY - 垂直偏移量
 */
export function moveLayerOffset(layer: ILayerProps, offsetX: number, offsetY: number) {
    const { left, top } = layer;
    layer.left = left + offsetX;
    layer.top = top + offsetY;
    (layer.children || []).forEach((item) => moveLayerOffset(item, offsetX, offsetY));
}

/**
 * 根据中心点，更新图层的位子
 *
 * @param layer - 图层属性
 * @param centerPoint - 中心点
 */
export const updateLayerByCenterPoint = (layer: ILayerProps, centerPoint: IPoint) => {
    const newLeft = centerPoint.x - layer.width / 2;
    const newTop = centerPoint.y - layer.height / 2;
    const offsetX = newLeft - layer.left;
    const offsetY = newTop - layer.top;
    moveLayerOffset(layer, offsetX, offsetY);
};

/**
 * 获取图层中心点
 *
 * @param layer - 图层属性
 */
export function getLayerCenterPoint(layer: ILayerProps) {
    return {
        x: layer.left + layer.width / 2,
        y: layer.top + layer.height / 2,
    };
}

/**
 *
 * @param identity frameworkIdentity或者外部dom的id
 * @param el
 * @param layerType
 * @returns
 */
export const checkElementIsLayerViewNode = (identity: string, el: HTMLElement, layerType: LayerType): boolean =>
    !!document.querySelector(`#${identity}`)?.contains(el) &&
    el.getAttribute('data-layer-type') === layerType &&
    !!el.getAttribute('data-layer-id');

/**
 *
 * @param identity frameworkIdentity或者外部dom的id
 * @param el
 * @returns
 */
export const getLayerIdAndLayerTypeByElement = (
    identity: string,
    el: HTMLElement,
): {
    layerId: string;
    layerType: string;
} | null => {
    if (!document.querySelector(`#${identity}`)?.contains(el)) return null;

    const fullAttr = el.getAttribute('data-layer-id');
    if (!fullAttr) return null;

    const hasParentStr = fullAttr.includes('-parent');
    const layerId = hasParentStr ? fullAttr.replace(/-parent$/, '') : fullAttr;
    const layerType = hasParentStr
        ? el.parentElement!.getAttribute('data-layer-type')
        : el.getAttribute('data-layer-type');

    if (!layerId || !layerType) return null;

    return { layerId, layerType };
};

/**
 * 获取图层视图节点
 *
 * @param identity - 元素 ID
 * @param layerId - 图层 ID
 */
export function getLayerViewNode(identity: string, layerId: string) {
    const parentId = `${layerId}-parent`;
    return (
        document.querySelector<HTMLElement>(`#${identity} div[data-layer-id="${parentId}"]`) ||
        document.querySelector<HTMLElement>(`#${identity} div[data-layer-id="${layerId}"]`)
    );
}

/**
 * 获取图层视图中心点
 *
 * @param identity - 元素 ID
 * @param layerId - 图层 ID
 */
export const getLayerViewCenterPointForDocument = (identity: string, layerId: string): IPoint => {
    const ele = getLayerViewNode(identity, layerId);
    if (!ele) return { x: 0, y: 0 };
    const rect = ele.getBoundingClientRect();
    return {
        x: rect.width / 2 + rect.left,
        y: rect.height / 2 + rect.top,
    };
};

/**
 * 检测矩形框是否在指定容器内
 *
 * @param layer - 图层属性
 * @param size - 尺寸大小
 */
export const checkRectInSize = (rect: IPosition & ISize, size: ISize) => {
    const { left, top, width, height } = rect;
    if (left + width > size.width || top + height > size.height) {
        return false;
    }
    if (left < 0 || top < 0) return false;
    return true;
};

/**
 * 检测矩形框是否完全不在指定容器内
 *
 * @param rect - 矩形框属性
 * @param size - 尺寸大小
 */
export const isRectOutOfBounds = (rect: IPosition & ISize, size: ISize) => {
    const { left, top, width, height } = rect;
    return (
        left + width < 0 || // 矩形框完全在左侧
        top + height < 0 || // 矩形框完全在上方
        left > size.width || // 矩形框完全在右侧
        top > size.height // 矩形框完全在下方
    );
};

/**
 * 转换原始图层坐标到舞台坐标
 *
 * @param point - 待转换的点
 * @param stageScale - 舞台缩放值
 */
export const convertOriginPointToLayer = (point: IPoint, stageScale: number): IPoint => {
    const { x, y } = point;
    return {
        x: x * stageScale,
        y: y * stageScale,
    };
};

/**
 * 转换舞台坐标到原始图层坐标
 *
 * @param point - 待转换的点
 * @param stageScale - 舞台缩放值
 */
export const convertLayerPointToOrigin = (point: IPoint, stageScale: number): IPoint => {
    const { x, y } = point;
    return {
        x: x / stageScale,
        y: y / stageScale,
    };
};

/**
 * 获取图层原始中心点
 *
 * @param size - 图层尺寸
 * @param position - 可选；图层位置
 */
export const getCenterPointForOrigin = (size: ISize | ILayerProps, position?: IPosition): IPoint => {
    let payload;
    if (!position) {
        payload = size as ILayerProps;
    } else {
        payload = {
            ...size,
            ...position,
        };
    }
    const { left, top, width, height } = payload;
    return {
        x: left + width / 2,
        y: top + height / 2,
    };
};

/**
 * 转换画笔图形为 SVG 图形
 *
 * @param layer - 图层属性
 */
export function convertBrushToVectorLayer(brush: ILayerProps) {
    const vectorData = convertBrushDataToVectorData(brush.data as IBrushLayerProps);
    if (!vectorData) return null;
    const vectorLayer = cloneDeep(brush);
    vectorLayer.data = vectorData;
    vectorLayer.layerType = LayerType.VECTOR_LAYER;
    return vectorLayer;
}

/**
 * 转换画笔数据为 SVG 数据
 *
 * @param brushData - 画笔数据
 */
export function convertBrushDataToVectorData(brushData: BrushData) {
    const canConvertType = [BrushType.Polygon, BrushType.Rect, BrushType.Ellipse];
    if (!canConvertType.includes(brushData.brushType)) return null;

    const isRect = BrushType.Rect === brushData.brushType;
    const isEllipse = BrushType.Ellipse === brushData.brushType;

    const isFillGradient = !isString(brushData.fills[0]?.color);
    const isStrokeGradient = !isString(brushData.stroke?.color);

    const vectorData = {
        url: brushData.url || '',
        colors: [],
        naturalWidth: brushData.naturalWidth,
        naturalHeight: brushData.naturalHeight,
        vectorType: isRect ? VectorType.Rect : isEllipse ? VectorType.Ellipse : VectorType.Polygon,
        fill: isFillGradient ? '' : (brushData.fills[0]?.color as string),
        fillGradient: isFillGradient ? (brushData.fills[0]?.color as ILinearGradient) : null,
        stroke: brushData.stroke?.width ? (isStrokeGradient ? '' : (brushData.stroke?.color as string)) : '',
        strokeGradient: brushData.stroke?.width
            ? isStrokeGradient
                ? (brushData.stroke?.color as ILinearGradient)
                : null
            : null,
        strokeWidth: brushData.stroke?.width || 0,
        strokeDasharray: brushData.stroke?.width ? brushData.stroke?.dasharray : '',
        strokeLinecap: brushData.stroke?.width ? brushData.stroke?.linecap : '',
        strokeHidden: false,
        radius: brushData.radius || 0,
        pickerIndex: null,
        blur: 0,
        stretchOption: StretchOptionType.fill,
        imageTransform: {
            offsetX: 0,
            offsetY: 0,
            rotate: 0,
            scale: 1,
            flipH: false,
            flipV: false,
            widthRatio: 1,
            heightRatio: 1,
        },
        effect: {
            shadow: [],
            reflection: [],
        },
        borderRadius: {
            leftTop: brushData.radius || 0,
            leftBottom: brushData.radius || 0,
            rightTop: brushData.radius || 0,
            rightBottom: brushData.radius || 0,
        },
    };
    return vectorData;
}

/**
 * 快速获取图层对象
 *
 * @param props - 图层属性
 */
export function getLayerProps(props: object): ILayerProps {
    return Object.assign(
        {
            id: uuid(),
            layerType: LayerType.GROUP_LAYER,
            width: 0,
            height: 0,
            left: 0,
            top: 0,
            rotate: 0,
            opacity: 1,
            title: '',
            data: {
                isExtraGroup: true,
            },
            lock: false,
            children: [],
            shine: false,
            hidden: false,
            category: 0,
            isAILayer: false,
            fitWidth: 0,
            fitHeight: 0,
            materialIds: {},
        },
        props,
    ) as ILayerProps;
}

/**
 * 快速获取分组对象
 *
 * @param props - 图层属性
 */
export function getGroupLayerProps(props: object): ILayerProps {
    return Object.assign(getLayerProps(props), { layerType: LayerType.GROUP_LAYER }) as ILayerProps;
}

/**
 * 快速获取文字对象
 *
 * @param props - 图层属性
 */
export function getTextLayerProps(props: object): ILayerProps {
    return Object.assign(getLayerProps(props), { layerType: LayerType.TEXT_LAYER }) as ILayerProps;
}

/**
 * 快速获取图片对象
 *
 * @param props - 图层属性
 */
export function getImgLayerProps(props: object) {
    return Object.assign(getLayerProps(props), { layerType: LayerType.IMAGE_LAYER }) as ILayerProps;
}

/**
 * 快速获 SVG 对象
 *
 * @param props - 图层属性
 */
export function getSvgLayerProps(props: object) {
    return Object.assign(getLayerProps(props), { layerType: LayerType.SVG_LAYER }) as ILayerProps;
}

/**
 * 快速获取主图对象
 *
 * @param props - 图层属性
 */
export function getMainLayerProps(props: object) {
    return Object.assign(getLayerProps(props), { layerType: LayerType.MAIN_LAYER }) as ILayerProps;
}

/**
 * 快速获取背景图层对象
 *
 * @param props - 图层属性
 */
export function getBackgroundLayerProps(props: object) {
    return Object.assign(getLayerProps(props), {
        layerType: LayerType.BACKGROUND_LAYER,
    }) as ILayerProps;
}

/**
 * 获取所有图层对应的 ID 为 key 的 map
 *
 * @param layers - 图层列表
 */
export function getLayersMap(layers: ILayerProps[]) {
    const map: { [key: string]: ILayerProps } = {};

    layers.forEach((topLayer) => {
        traverseTree(topLayer, (layer: ILayerProps) => {
            map[layer.id] = layer;
        });
    });

    return map;
}

export function getLayersArray(layers: ILayerProps[]) {
    const layerArray: ILayerProps[] = [];
    layers.forEach((topLayer) => {
        traverseTree(topLayer, (layer: ILayerProps) => {
            layerArray.push(layer);
        });
    });

    return layerArray;
}

interface IAdjustCropImgPositionProps {
    mainLayerSize: ISize; // 外层大小
    adjustLayerSize: ISize; // 调整的图层大小
    adjustLayerRotate: number; // 调整的图层旋转角度
    adjustLayerRelativePosition: IPoint; // 图层相对于外层的位置
}

/**
 * 裁剪回弹
 *
 * @param props - 裁剪属性
 */
export function adjustCropImgPosition({
    mainLayerSize,
    adjustLayerSize,
    adjustLayerRotate,
    adjustLayerRelativePosition,
}: IAdjustCropImgPositionProps) {
    const { width: mainLayerWidth, height: mainLayerHeight } = mainLayerSize;
    const { width: layerWidth, height: layerHeight } = adjustLayerSize;
    const { x: offsetX, y: offsetY } = adjustLayerRelativePosition;

    // 外框 相对于 图层的位置
    const mainLayerLeftTopPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: -mainLayerWidth / 2,
                    y: mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );

    const mainLayerRightTopPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: mainLayerWidth / 2,
                    y: mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );
    const mainLayerLeftBottomPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: -mainLayerWidth / 2,
                    y: -mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );
    const mainLayerRightBottomPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: mainLayerWidth / 2,
                    y: -mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );

    const halfLayerWidth = layerWidth / 2;
    const halfLayerHeight = layerHeight / 2;

    const xRange = {
        min: -halfLayerWidth,
        max: halfLayerWidth,
    };

    const yRange = {
        min: -halfLayerHeight,
        max: halfLayerHeight,
    };

    const points = [mainLayerLeftTopPoint, mainLayerRightTopPoint, mainLayerLeftBottomPoint, mainLayerRightBottomPoint];

    const overflowX = {
        max: points[0].x - xRange.max,
        min: points[0].x - xRange.min,
    };

    const overflowY = {
        max: points[0].y - yRange.max,
        min: points[0].y - yRange.min,
    };

    // 算出是否超出边界
    points.forEach(({ x, y }) => {
        const cutMaxX = x - xRange.max;
        const cutMinX = x - xRange.min;

        const cutMaxY = y - yRange.max;
        const cutMinY = y - yRange.min;

        overflowX.max = cutMaxX > overflowX.max ? cutMaxX : overflowX.max;
        overflowX.min = cutMinX < overflowX.min ? cutMinX : overflowX.min;

        overflowY.max = cutMaxY > overflowY.max ? cutMaxY : overflowY.max;
        overflowY.min = cutMinY < overflowY.min ? cutMinY : overflowY.min;
    });

    let scaleX = 1;
    let scaleY = 1;

    // 超出边界能否移回来 移不回来就要放大
    if (
        (overflowX.max > 0 && overflowX.min < 0) || // 两边都超出
        (overflowX.max > 0 && overflowX.max - overflowX.min > 0) || // 右边超出太多 回弹还是会超出
        (overflowX.min < 0 && overflowX.min - overflowX.max < 0) // 左边超出  回弹还是会超出
    ) {
        let scaleDistance = 0;
        if (overflowX.max > 0 && overflowX.min < 0) {
            scaleDistance = Math.max(overflowX.max, Math.abs(overflowX.min));
        } else if (overflowX.max > 0 && overflowX.max - overflowX.min > 0) {
            scaleDistance = overflowX.max - overflowX.min;
        } else if (overflowX.min < 0 && overflowX.min - overflowX.max < 0) {
            scaleDistance = Math.abs(overflowX.max - overflowX.min);
        }

        scaleX = 1 + (toInteger(scaleDistance) * 2) / layerWidth;
    }

    if (
        (overflowY.max > 0 && overflowY.min < 0) || // 两边都超出
        (overflowY.max > 0 && overflowY.max - overflowY.min > 0) || // 右边超出太多 回弹还是会超出
        (overflowY.min < 0 && overflowY.min - overflowY.max < 0) // 左边超出  回弹还是会超出
    ) {
        let scaleDistance = 0;
        if (overflowY.max > 0 && overflowY.min < 0) {
            scaleDistance = Math.max(overflowY.max, Math.abs(overflowY.min));
        } else if (overflowY.max > 0 && overflowY.max - overflowY.min > 0) {
            scaleDistance = overflowY.max - overflowY.min;
        } else if (overflowY.min < 0 && overflowY.min - overflowY.max < 0) {
            scaleDistance = Math.abs(overflowY.min - overflowY.max);
        }

        scaleY = 1 + (toInteger(scaleDistance) * 2) / layerHeight;
    }

    const scale = scaleX > scaleY ? scaleX : scaleY;

    let movePoint = {
        x: 0,
        y: 0,
    };

    if (scale === 1) {
        if (overflowX.max > 0) {
            movePoint.x = overflowX.max;
        } else if (overflowX.min < 0) {
            movePoint.x = overflowX.min;
        }

        if (overflowY.max > 0) {
            movePoint.y = overflowY.max;
        } else if (overflowY.min < 0) {
            movePoint.y = overflowY.min;
        }
    }

    movePoint = translatePointAngle(movePoint, -adjustLayerRotate);

    return {
        scale,
        movePoint,
    };
}

/**
 * 获取素材 ID 列表
 *
 * @param materialIds - 素材 ID 列表
 * @param exclude - 可选；要排除的素材 ID
 */
export const getMaterialIds = (materialIds: Record<string, string[]> | string[], exclude?: string[]) => {
    let ids: string[] = [];
    exclude = exclude || [];
    if (Object.prototype.toString.call(materialIds) === '[object Object]') {
        Object.keys(materialIds).forEach((key) => {
            if (!exclude!.includes(key)) ids = ids.concat(materialIds[key]);
        });
    }
    return ids;
};

/**
 * 递归返回合并的新图层
 *
 * @param layer - 图层属性
 * @param cb - 回调处理方法
 */
export const generateNewLayer = async (layer: ILayerProps, cb: Function, ...rest: any[]) => {
    if (layer.layerType === LayerType.GROUP_LAYER) {
        const children = await Promise.all(
            layer.children.map(async (childLayer) => {
                const result = await generateNewLayer(childLayer, cb, ...rest);
                return result;
            }),
        );
        return {
            ...layer,
            children,
        };
    } else {
        return await cb(layer.id, ...rest);
    }
};

/**
 * 添加图层的素材 ID
 *
 * @param layer - 图层属性
 * @param materiaType - 素材类型
 * @param materialId - 素材 ID
 */
export const addMaterialId = (layer: ILayerProps, materiaType: MaterialIdsType, materialId: string) => {
    if (!layer.materialIds) {
        layer.materialIds = {};
    }

    const materialIds = layer.materialIds[materiaType] || [];

    materialIds.push(materialId);
    layer.materialIds[materiaType] = materialIds;
};

/**
 * 移除图层的素材 ID
 *
 * @param layer - 图层属性
 * @param materiaType - 素材类型
 * @param materialId - 素材 ID
 */
export const removeMaterialId = (layer: ILayerProps, materiaType: MaterialIdsType, materialId: string) => {
    if (!layer.materialIds) {
        return;
    }
    const materialIds = layer.materialIds[materiaType] || [];
    const index = materialIds.indexOf(materialId);
    if (index !== -1) {
        materialIds.splice(index, 1);
        layer.materialIds[materiaType] = materialIds;
    }
};

/**
 * 获取 SVG 图片样式
 *
 * @param width - 宽度
 * @param height - 高度
 */
export function getLayerImageStyle(width: number, height: number) {
    const imageStyle: React.CSSProperties = {
        width: `${width}px`,
        height: `${height}px`,
        pointerEvents: 'none',
        display: 'block',
    };
    return imageStyle;
}

/**
 * 获取内层样式
 *
 * @param width - 宽度
 * @param height - 高度
 * @param offsetX - 水平偏移量
 * @param offsetY - 垂直偏移量
 * @param scale - 缩放比例
 * @param flipH - 水平翻转值
 * @param flipV - 垂直翻转值
 */
export function getLayerInnerStyle(
    width: number,
    height: number,
    offsetX: number,
    offsetY: number,
    scale: number,
    flipH: boolean,
    flipV: boolean,
) {
    let rotate = '';
    // 翻转
    if (flipH) {
        rotate = 'rotateY(180deg) ';
    }
    if (flipV) {
        const ry180 = 'rotateX(180deg)';
        rotate = rotate ? rotate + ry180 : ry180;
    }

    const imageInnerStyle: React.CSSProperties = {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${offsetX}px, ${offsetY}px) ${rotate} scale(${+scale.toFixed(3)})`,
        transformOrigin: 'center center',
    };
    return imageInnerStyle;
}

/**
 * 获取外层样式
 *
 * @param opacity - 可选；透明度；默认值为 `1`，即不透明
 */
export function getLayerWrapStyle(opacity = 1) {
    const imageWrapStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        opacity,
    };
    return imageWrapStyle;
}

/**
 * 获取翻转样式
 *
 * @param flipH - 可选；水平翻转；默认值为 `false`
 * @param flipV - 可选；垂直翻转；默认值为 `false`
 */
export function getLayerFlipStyle(flipH = false, flipV = false) {
    const flipStyle: React.CSSProperties = {
        pointerEvents: 'none',
    };
    // 翻转
    if (flipH) {
        flipStyle.transform = 'rotateY(180deg) ';
    }
    if (flipV) {
        const ry180 = 'rotateX(180deg)';
        flipStyle.transform = flipStyle.transform ? flipStyle.transform + ry180 : ry180;
    }

    return flipStyle;
}

/**
 * 获取操作的中心点
 *
 * @param originInfo - 源信息
 * @param newSize - 新的尺寸大小
 * @param reverse - 是否反转
 * @param direction - 方向
 */
export const getNewCenterPoint = (
    originInfo: IPosition & ISize & { rotate: number },
    newSize: ISize,
    reverse: boolean,
    direction: number,
) => {
    const { rotate, width: originWidth, height: originHeight } = originInfo;
    const { width, height } = newSize;
    const offsetX = width - originWidth;
    const offsetY = height - originHeight;
    const originCenter = getCenterPointForOrigin(originInfo);
    const heightDirection = reverse ? 1 : -1;
    const resizeHeightNewCenter = addPoint(originCenter, {
        x: heightDirection * (offsetY / 2) * Math.cos(((90 - rotate) / 180) * Math.PI) * direction,
        y: -heightDirection * (offsetY / 2) * Math.sin(((90 - rotate) / 180) * Math.PI) * direction,
    });

    const resizeWidthNewCenter = getCenterPointForOrigin({
        left: resizeHeightNewCenter.x - originWidth / 2,
        top: resizeHeightNewCenter.y - height / 2,
        width: originWidth,
        height: height,
    });
    return addPoint(resizeWidthNewCenter, {
        x: (offsetX / 2) * Math.cos((rotate / 180) * Math.PI) * direction,
        y: (offsetX / 2) * Math.sin((rotate / 180) * Math.PI) * direction,
    });
};

export const convertLayerInfoToPoints = (info: Pick<ILayerInfo, 'left' | 'top' | 'width' | 'height' | 'id'>) => {
    const { left, top, width, height, id } = info;
    const right = left + width;
    const bottom = top + height;
    const halfWidth = Math.floor(width / 2);
    const halfHeight = Math.floor(height / 2);
    const centerX = left + halfWidth;
    const centerY = top + halfHeight;

    const lt = { x: left, y: top };
    const lb = { x: left, y: bottom };
    const rt = { x: right, y: top };
    const rb = { x: right, y: bottom };
    const tc = { x: centerX, y: top };
    const bc = { x: centerX, y: bottom };
    const lc = { x: left, y: centerY };
    const rc = { x: right, y: centerY };

    return { lt, lb, rt, rb, tc, bc, lc, rc, id } as IPointsInfo;
};

export const convertLayerInfoToKeyAxis = (
    info: Pick<ILayerInfo, 'left' | 'top' | 'width' | 'height' | 'id'>,
    ignoreCenter: boolean = false,
) => {
    const { left, top, width, height, id } = info;
    const right = left + width;
    const bottom = top + height;

    if (ignoreCenter) {
        return {
            x: [left, right],
            y: [top, bottom],
            id,
        };
    }
    const centerX = Math.floor(left + width / 2);
    const centerY = Math.floor(top + height / 2);

    return {
        x: [left, centerX, right],
        y: [top, centerY, bottom],
        id,
    };
};

/**
 * 获取旋转后的图层 rect 信息
 *
 * @param layer - 图层属性
 */
export const getLayerInfoWithRotate = (layer: ITransform, centerPoint?: IPoint) => {
    const points = getRotatePosition(layer, centerPoint);
    const minX = minBy(points, (pos) => pos.x) as IPoint;
    const maxX = maxBy(points, (pos) => pos.x) as IPoint;
    const minY = minBy(points, (pos) => pos.y) as IPoint;
    const maxY = maxBy(points, (pos) => pos.y) as IPoint;

    const left = Math.floor(minX?.x ?? 0);
    const top = Math.floor(minY?.y ?? 0);
    const width = Math.floor((maxX?.x ?? 0) - (minX?.x ?? 0));
    const height = Math.floor((maxY?.y ?? 0) - (minY?.y ?? 0));

    return { left, top, width, height, id: layer.id } as ILayerInfo;
};

/**
 * 遍历所有图层
 *
 * @param data - 要遍历的图层列表
 * @param activeLayer - 激活的图层(遍历时会过滤激活的图层)
 * @param callback - 回调函数
 */
export const traverseLayers = (
    data: ILayerProps[],
    activeLayer: ILayerProps,
    callback: Function,
    recursive: boolean = true,
) => {
    let j = 0;
    const MaxLayer = 100;
    function filterLayer(data: ILayerProps[]) {
        for (let i = 0, len = data.length; i < len; i++) {
            if (j >= MaxLayer) break;
            const item = data[i];
            // 过滤激活的图层
            if (
                item.id === activeLayer.id ||
                item.layerType === LayerType.BACKGROUND_LAYER ||
                item.layerType === LayerType.MAIN_LAYER
            ) {
                continue;
            }
            if (recursive && item.children && item.children.length) {
                if (item.layerType !== LayerType.GROUP_LAYER) {
                    callback(item);
                    j++;
                }
                filterLayer(item.children);
            } else {
                callback(item);
                j++;
            }
        }
    }

    filterLayer(data);
};

/**
 * 获取页面渲染的 Canvas
 *
 * @param id - 画布 ID
 */
export const getCanvasById = (id: string) => {
    let canvas = document.getElementById(`${IMAGE_LAYER_CANVAS_ID_PREFIX}${id}`) as HTMLCanvasElement;
    if (!canvas) {
        canvas = document.querySelector(
            `[data-canvas-id=${IMAGE_LAYER_CANVAS_ID_PREFIX}${id}]`,
        ) as unknown as HTMLCanvasElement;
    }
    const { width, height } = canvas || { width: 0, height: 0 };
    return {
        width,
        height,
        canvas,
        ctx: canvas?.getContext('2d'),
    };
};

/**
 * 拷贝页面渲染的 Canvas
 *
 * @param id - 画布 ID
 */
export const copyCanvasById = (id: string): HTMLCanvasElement => {
    const { canvas, width, height } = getCanvasById(id);
    return createCanvas(width, height, canvas).canvas;
};

/** 获取差异属性 */
export function getDifferentProps(
    newLayer: ILayerProps,
    oldLayer: ILayerProps,
    differentLayerProps: Record<string, any> = {},
    skipAttrs: Set<string> = new Set(),
) {
    const differentProps: any = {};

    if (!newLayer || !oldLayer) {
        return differentLayerProps;
    }

    // console.log('ot getDifferentProps 1', newLayer, oldLayer);

    for (const key in newLayer) {
        if (key.startsWith('_') || skipAttrs.has(key)) continue;

        if (key === 'children') {
            const newChildren = newLayer.children || [];
            const oldChildren = oldLayer.children || [];
            const maxLength = Math.max(newChildren.length, oldChildren.length);

            for (let i = 0; i < maxLength; i++) {
                const newChild = newChildren[i];
                const oldChild = oldChildren[i];

                if (newChild && oldChild) {
                    getDifferentProps(newChild, oldChild, differentLayerProps, skipAttrs);
                } else if (newChild) {
                    differentLayerProps[newChild.id] = { ...newChild };
                } else if (oldChild) {
                    differentLayerProps[oldChild.id] = { deleted: true };
                }
            }
            continue;
        }

        if (typeof newLayer[key] === 'object' && newLayer[key] !== null) {
            if (Array.isArray(newLayer[key])) {
                // 数组比对
                if (!Array.isArray(oldLayer[key]) || !areArraysEqual(newLayer[key], oldLayer[key])) {
                    differentProps[key] = JSON.parse(JSON.stringify(newLayer[key]));
                }
            } else {
                // 对象比对 - 细分到属性级别
                const newObj = newLayer[key];
                const oldObj = oldLayer[key];

                if (!oldObj || typeof oldObj !== 'object') {
                    // 如果旧值不存在或不是对象，记录整个新对象
                    differentProps[key] = JSON.parse(JSON.stringify(newObj));
                } else {
                    // 逐个属性比对
                    const propDiffs = {};
                    let hasDiffs = false;

                    for (const prop in newObj) {
                        // 特殊处理 svg 图层添加文字逻辑， data.textInfo
                        if (newLayer.layerType === LayerType.VECTOR_LAYER && key === 'data' && prop === 'textInfo') {
                            const newTextInfo = (newLayer[key] as unknown as IVectorLayerProps).textInfo;
                            const oldTextInfo = (oldLayer[key] as unknown as IVectorLayerProps)?.textInfo;
                            const oldStrTextInfo = JSON.stringify(oldTextInfo);
                            const newStrTextInfo = JSON.stringify(newTextInfo);
                            if (oldStrTextInfo !== newStrTextInfo) {
                                propDiffs[prop] = JSON.parse(newStrTextInfo);
                                hasDiffs = true;
                                continue;
                            }
                        }
                        if (typeof newObj[prop] === 'object' && newObj[prop] !== null) {
                            if (Array.isArray(newObj[prop])) {
                                if (!Array.isArray(oldObj[prop]) || !areArraysEqual(newObj[prop], oldObj[prop])) {
                                    propDiffs[prop] = JSON.parse(JSON.stringify(newObj[prop]));
                                    hasDiffs = true;
                                }
                            } else {
                                // 递归比对嵌套对象
                                const nestedDiffs = compareObjects(newObj[prop], oldObj[prop]);
                                if (Object.keys(nestedDiffs).length > 0) {
                                    propDiffs[prop] = nestedDiffs;
                                    hasDiffs = true;
                                }
                            }
                        } else if (newObj[prop] !== oldObj[prop]) {
                            propDiffs[prop] = newObj[prop];
                            hasDiffs = true;
                        }
                    }

                    // 检查是否有属性被删除
                    for (const prop in oldObj) {
                        if (!(prop in newObj)) {
                            propDiffs[prop] = undefined; // 标记被删除的属性
                            hasDiffs = true;
                        }
                    }

                    if (hasDiffs) {
                        differentProps[key] = propDiffs;
                    }
                }
            }
        } else if (newLayer[key] !== oldLayer[key]) {
            differentProps[key] = newLayer[key];
        }
    }

    // 检查是否有属性被删除
    for (const key in oldLayer) {
        if (!key.startsWith('_') && key !== 'children' && !(key in newLayer)) {
            differentProps[key] = undefined; // 标记被删除的属性
        }
    }

    if (Object.keys(differentProps).length > 0) {
        if (isNumber(differentProps.height) || isNumber(differentProps.width)) {
            differentProps.height = newLayer.height;
            differentProps.width = newLayer.width;
        }

        // 处理箭头标注图层，锚点跟高度需同时同步
        if (newLayer.layerType === LayerType.MARKUP_LAYER) {
            if (isNumber(differentProps.height) || isNumber(differentProps.width) || differentProps.data?.anchor) {
                const data = newLayer.data as IMarkupLayerProps;
                if (data.markupType === MarkupType.Arrow) {
                    differentProps.data = differentProps.data || {};
                    differentProps.data.anchor = data.anchor;
                    differentProps.left = newLayer.left;
                    differentProps.top = newLayer.top;
                }
            }
        }

        differentLayerProps[newLayer.id] = replaceUndefinedWithFlag(differentProps);
    }

    // console.log('ot getDifferentProps 2', differentLayerProps);

    return differentLayerProps;
}

// 替换 undefined 为 delPropsFlag, 用于标识删除的属性
export const delPropsFlag = ':d-e_l';
function replaceUndefinedWithFlag(obj: any): any {
    if (obj === undefined) {
        return delPropsFlag;
    }
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => replaceUndefinedWithFlag(item));
    }
    const result = {};
    for (const key in obj) {
        result[key] = replaceUndefinedWithFlag(obj[key]);
    }
    return result;
}

/** 应用差异属性 */
export function applyDifferentProps(
    layer: ILayerProps,
    differentProps: Record<string, any>,
    runtimeLayer?: ILayerProps,
    isRoot = true,
) {
    let hasChanges = false; // 标记是否有属性发生变化

    // svg 文本 编辑时状态下，接应用文字，会导致编辑层跟渲染层不一致，需要特殊处理
    if (isRoot && runtimeLayer && runtimeLayer.__isOnTextEditing) {
        if (!layer.__renderTextVersion) {
            layer.__renderTextVersion = 0;
        }
        if (runtimeLayer.layerType === LayerType.VECTOR_LAYER && differentProps?.data?.textInfo) {
            delete differentProps.data.textInfo.text;
            delete differentProps.data.textInfo.fontFamily;
            layer.__renderTextVersion++;
        }
        if (runtimeLayer.layerType === LayerType.TEXT_LAYER) {
            if (differentProps?.data?.text) {
                delete differentProps.data.text;
                layer.__renderTextVersion++;
            }
            if (differentProps?.data?.fontFamily) {
                delete differentProps.data.fontFamily;
                delete differentProps.materialIds?.['font-family'];
                layer.__renderTextVersion++;
            }
            if (differentProps?.data?.textContents) {
                delete differentProps.data.textContents;
                delete differentProps.materialIds?.['font-family'];
                delete differentProps.materialIds?.['text-mark'];
                layer.__renderTextVersion++;
            }
            if (differentProps?.data?.textMark) {
                delete differentProps.data.textMark;
                delete differentProps.materialIds?.['text-mark'];
                layer.__renderTextVersion++;
            }
        }
    }

    // 处理每个变更的属性
    for (const key in differentProps) {
        const value = differentProps[key];

        // 处理删除的情况
        if (value === delPropsFlag) {
            delete layer[key];
            hasChanges = true;
            continue;
        }

        // 处理对象类型的属性
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // 如果原来不存在这个属性或不是对象，直接创建新对象
            if (!layer[key] || typeof layer[key] !== 'object') {
                layer[key] = {};
                hasChanges = true;
            }
            // 递归应用嵌套的属性变更
            const nestedHasChanges = applyDifferentProps(layer[key], value, runtimeLayer, false);
            if (nestedHasChanges) {
                hasChanges = true;
            }
        } else if (Array.isArray(value)) {
            // 处理数组类型 - 使用 JSON.stringify 比较
            if (!Array.isArray(layer[key]) || JSON.stringify(layer[key]) !== JSON.stringify(value)) {
                layer[key] = value;
                hasChanges = true;
            }
        } else {
            // 处理基本类型 - 只有当值不同时才赋值
            if (layer[key] !== value) {
                layer[key] = value;
                hasChanges = true;

                // 处理矢量图层宽度、高度属性, 用于实时计算文字排版
                if (layer.layerType === LayerType.VECTOR_LAYER) {
                    if (key === 'width') {
                        layer.__proactiveWidth = value;
                    }
                    if (key === 'height') {
                        layer.__proactiveHeight = value;
                    }
                }
            }
        }
    }

    return hasChanges; // 返回是否有属性被赋值过
}

function compareObjects(newObj: object, oldObj: object): object {
    if (!oldObj || typeof oldObj !== 'object') {
        return JSON.parse(JSON.stringify(newObj));
    }

    const diffs = {};
    let hasDiffs = false;

    // 检查新值中的属性
    for (const prop in newObj) {
        if (typeof newObj[prop] === 'object' && newObj[prop] !== null) {
            if (Array.isArray(newObj[prop])) {
                if (!Array.isArray(oldObj[prop]) || !areArraysEqual(newObj[prop], oldObj[prop])) {
                    diffs[prop] = JSON.parse(JSON.stringify(newObj[prop]));
                    hasDiffs = true;
                }
            } else {
                const nestedDiffs = compareObjects(newObj[prop], oldObj[prop]);
                if (Object.keys(nestedDiffs).length > 0) {
                    diffs[prop] = nestedDiffs;
                    hasDiffs = true;
                }
            }
        } else if (newObj[prop] !== oldObj[prop]) {
            diffs[prop] = newObj[prop];
            hasDiffs = true;
        }
    }

    // 检查被删除的属性
    for (const prop in oldObj) {
        if (!(prop in newObj)) {
            diffs[prop] = undefined;
            hasDiffs = true;
        }
    }

    return hasDiffs ? diffs : {};
}

function areArraysEqual(arr1: object[], arr2: object[]): boolean {
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item, index) => {
        if (typeof item === 'object' && item !== null) {
            if (Array.isArray(item)) {
                return Array.isArray(arr2[index]) && areArraysEqual(item as object[], arr2[index] as object[]);
            }
            return Object.keys(compareObjects(item, arr2[index])).length === 0;
        }
        return item === arr2[index];
    });
}

/**
 * 指定图层 id 创建 Canvas
 *
 * @param id - 图层 id
 * @param url - 图片 url
 */
export async function createFromCanvas(id: string, url: string) {
    const { img, width, height } = await getImgInfoAsync(url);
    const originImg = createCanvas(width, height, img);
    const { width: lWidth, height: lHeight } = getCanvasById(id);
    return createCanvas(lWidth, lHeight, originImg.canvas).canvas;
}

/**
 * 获取图层包围盒
 *
 * @param layer - 图层
 */
export const getLayerBoundingBox = (layer: ILayerProps) => {
    const { left, top, width, height, rotate } = layer;

    const centerPoint = {
        x: left + width / 2,
        y: top + height / 2,
    };

    const points = [
        translatePointMove(
            rotatePoint(
                {
                    x: left - centerPoint.x,
                    y: top - centerPoint.y,
                },
                rotate,
            ),
            centerPoint.x,
            centerPoint.y,
        ),

        translatePointMove(
            rotatePoint(
                {
                    x: left + width - centerPoint.x,
                    y: top - centerPoint.y,
                },
                rotate,
            ),
            centerPoint.x,
            centerPoint.y,
        ),

        translatePointMove(
            rotatePoint(
                {
                    x: left - centerPoint.x,
                    y: top + height - centerPoint.y,
                },
                rotate,
            ),
            centerPoint.x,
            centerPoint.y,
        ),

        translatePointMove(
            rotatePoint(
                {
                    x: left + width - centerPoint.x,
                    y: top + height - centerPoint.y,
                },
                rotate,
            ),
            centerPoint.x,
            centerPoint.y,
        ),
    ].map((point) => [point.x, point.y]);

    return points;
};

export const computerImageLayerRatio = (
    layerWidth: number,
    layerHeight: number,
    imageWidth: number,
    imageHeight: number,
) => {
    // Calculate aspect ratios
    const layerAspectRatio = layerWidth / layerHeight;
    const imageAspectRatio = imageWidth / imageHeight;

    let widthRatio, heightRatio, scale;

    if (imageAspectRatio >= layerAspectRatio) {
        // Image is wider relative to its height than the layer
        heightRatio = 1;
        widthRatio = imageAspectRatio / layerAspectRatio;
        scale = imageHeight / (layerHeight * heightRatio);
    } else {
        // Image is taller relative to its width than the layer
        widthRatio = 1;
        heightRatio = layerAspectRatio / imageAspectRatio;
        scale = imageWidth / (layerWidth * widthRatio);
    }

    return {
        widthRatio,
        heightRatio,
        scale,
    };
};

export const deleteExtraInfo = (layer: ILayerProps) => {
    const extraInfo = layer.extraInfo;
    if (extraInfo && extraInfo.lockRatio === true) {
        delete layer.extraInfo!.lockRatio;
    }

    if (extraInfo && extraInfo.modified !== undefined) {
        delete layer.extraInfo!.modified;
    }

    if (extraInfo && Object.keys(extraInfo).length === 0) {
        delete layer.extraInfo;
    }
};

// 获取图层最新的DOM节点
export function getLayerFreshDOM(selector: string): HTMLElement | undefined {
    const lastNode = Array.prototype.at.call(document.querySelectorAll(selector), -1);
    // 适配小图层有响应区域场景
    if (lastNode?.classList.contains('reactive-area')) {
        const newSelector = selector.replace(/"]$/, '-parent"]');
        return Array.prototype.at.call(document.querySelectorAll(newSelector), -1);
    }
    return lastNode;
}
